import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Link, ExternalLink, Plus, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface TaskLinksProps {
  taskId: string;
  projectTaskId?: string;
  organizationId: string;
  userRole?: string | null;
}

export const TaskLinks = ({ taskId, projectTaskId, organizationId, userRole }: TaskLinksProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newLink, setNewLink] = useState({ title: "", url: "" });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: links, isLoading } = useQuery({
    queryKey: ["task-links", taskId, projectTaskId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("task_links")
        .select("*")
        .or(`task_id.eq.${taskId}${projectTaskId ? `,project_task_id.eq.${projectTaskId}` : ""}`)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const addLinkMutation = useMutation({
    mutationFn: async (linkData: { title: string; url: string }) => {
      const { data: session } = await supabase.auth.getSession();
      
      const data = {
        task_id: taskId,
        project_task_id: projectTaskId,
        title: linkData.title,
        url: linkData.url,
        organization_id: organizationId,
        ...(session.session?.user 
          ? { created_by: session.session.user.id }
          : { created_by_guest: "Guest User" }
        ),
      };

      const { data: result, error } = await supabase
        .from("task_links")
        .insert([data])
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-links", taskId, projectTaskId] });
      setNewLink({ title: "", url: "" });
      setIsDialogOpen(false);
      toast({
        title: "Enlace agregado",
        description: "El enlace ha sido agregado exitosamente.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo agregar el enlace.",
        variant: "destructive",
      });
    },
  });

  const deleteLinkMutation = useMutation({
    mutationFn: async (linkId: string) => {
      const { error } = await supabase
        .from("task_links")
        .delete()
        .eq("id", linkId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-links", taskId, projectTaskId] });
      toast({
        title: "Enlace eliminado",
        description: "El enlace ha sido eliminado exitosamente.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo eliminar el enlace.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newLink.title.trim() && newLink.url.trim()) {
      addLinkMutation.mutate(newLink);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link className="h-4 w-4" />
          <h3 className="font-medium">Enlaces</h3>
          <span className="text-sm text-muted-foreground">
            ({links?.length || 0})
          </span>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Agregar enlace
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Agregar nuevo enlace</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium">Título</label>
                <Input
                  value={newLink.title}
                  onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                  placeholder="Nombre del enlace"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">URL</label>
                <Input
                  value={newLink.url}
                  onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                  placeholder="https://ejemplo.com"
                  type="url"
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={addLinkMutation.isPending}>
                  {addLinkMutation.isPending ? "Agregando..." : "Agregar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Links List */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Cargando enlaces...</div>
        ) : links?.length === 0 ? (
          <div className="text-sm text-muted-foreground">No hay enlaces aún.</div>
        ) : (
          links?.map((link) => (
            <Card key={link.id} className="p-3">
              <CardContent className="p-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-primary hover:underline truncate block"
                      >
                        {link.title}
                      </a>
                      <p className="text-xs text-muted-foreground truncate">
                        {link.url}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteLinkMutation.mutate(link.id)}
                    disabled={deleteLinkMutation.isPending}
                    className="flex-shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};